/**
 * ALERTY O NOWYCH MIESZKANIACH — REGUŁY PRZEŁĄCZNIKA (build plan C6/C7, plan §1.29–1.32,
 * §1.64, §1.65; `docs/AlertEmails.md` §1, §4).
 *
 * Jedno ustawienie, `flat.saved_searches.alerts_enabled`: przełącznik w Ustawieniach →
 * Powiadomienia i pierwszy szczebel „Usuń konto” to TO SAMO pole (hook `useAlertyMieszkan`).
 * Bez pauzy (§1.29). Wyłączenie zostawia kadencję (§1.30). Bez zapisanego zbioru (`saved_at` null)
 * nie ma czego wysyłać — przełącznik jest wyłączony i nieaktywny (RPC i tak odmawia, F1).
 *
 * Czyste funkcje, bez Reacta i Supabase — testowane osobno (`alertyMieszkan.test.ts`).
 */

/** Po tylu dniach kalendarzowych bez aktywności e-maile wyłączają się same (§1.31, AlertEmails §4). */
export const DNI_BEZCZYNNOSCI = 15;

export interface StanAlertow {
  savedAt: string | null;
  enabled: boolean;
  lastActiveAt: string | null;
}

/** Przełącznik można dotknąć tylko przy zapisanym zbiorze (§1.64: inaczej „nie ma czego wysłać”). */
export function moznaPrzelaczyc(s: Pick<StanAlertow, 'savedAt'>): boolean {
  return s.savedAt !== null;
}

// Raz: budowa formatera Intl kosztuje wielokrotnie więcej niż jego użycie (wysyłka woła to per zestaw).
const FORMAT_DNIA = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit' });

/** Data kalendarzowa w Warszawie (`YYYY-MM-DD`) — dni liczymy po polsku, nie w UTC. */
export function dzienWarszawa(d: Date): string {
  return FORMAT_DNIA.format(d);
}

/** Dni kalendarzowe (czas warszawski) od `iso` do `teraz`; `null` dla braku albo złej daty. */
export function dniOd(iso: string | null, teraz: Date = new Date()): number | null {
  if (!iso) return null;
  const od = new Date(iso);
  if (Number.isNaN(od.getTime())) return null;
  const a = Date.parse(dzienWarszawa(od) + 'T00:00:00Z');
  const b = Date.parse(dzienWarszawa(teraz) + 'T00:00:00Z');
  return Math.round((b - a) / 86_400_000);
}

/** Dzień ostrzeżenia „Za 3 dni wyłączymy…” (§1.31, AlertEmails §3.3). */
export const DNI_OSTRZEZENIA = 12;

/**
 * Dlaczego e-maile są wyłączone — zapisuje to ten, kto je wyłączył (`flat.saved_searches.alerts_off_reason`,
 * PreLaunchChecklist 5.13): `inactivity` = wysyłka po 15 dniach bez aktywności (C8), `unsubscribe` =
 * link „Wyłącz alerty” albo przycisk rezygnacji w programie pocztowym. Przełącznik w
 * Ustawieniach i każde włączenie czyszczą powód (`flat.save_my_filters`).
 */
export type PowodWylaczenia = 'inactivity' | 'unsubscribe';

/**
 * Notka „Wyłączyliśmy e-maile, bo od 15 dni nie zaglądałeś/aś…” (§1.64): zbiór zapisany, e-maile
 * wyłączone, a ZAPISANY powód to bezczynność. Do C8 notka zgadywała po `last_active_at`; teraz czyta
 * powód, więc ręczne wyłączenie i porzucenie konta jej nie pokazuje, a wizyta, która odświeża
 * `last_active_at`, jej nie gasi. Znika przy włączeniu (RPC czyści powód).
 */
export function wylaczoneZBezczynnosci(s: Pick<StanAlertow, 'savedAt' | 'enabled'> & { offReason: string | null }): boolean {
  return s.savedAt !== null && !s.enabled && s.offReason === 'inactivity';
}

/**
 * Co reguła 15 dni robi z włączonymi e-mailami (wysyłka C8, AlertEmails §4): dni kalendarzowe od
 * ostatniej aktywności i NIC więcej — wysłane alerty się nie liczą.
 *  - `wylacz`  — ≥ 15 dni: e-mail „Wyłączyliśmy…” i `alerts_enabled = false`;
 *  - `ostrzez` — 12–14 dni i od tej aktywności jeszcze nie ostrzegaliśmy: e-mail „Za 3 dni…”;
 *  - `null`    — nic.
 * `aktywnoscOd` = `last_active_at` (włączenie e-maili też je stempluje); `null` = nic nie wiadomo → nic.
 */
export function etapBezczynnosci(
  aktywnoscOd: string | null,
  ostrzezonoAt: string | null,
  teraz: Date = new Date(),
): 'wylacz' | 'ostrzez' | null {
  const dni = dniOd(aktywnoscOd, teraz);
  if (dni === null) return null;
  if (dni >= DNI_BEZCZYNNOSCI) return 'wylacz';
  if (dni < DNI_OSTRZEZENIA) return null;
  const juz = ostrzezonoAt ? Date.parse(ostrzezonoAt) : NaN;
  return Number.isFinite(juz) && juz >= Date.parse(aktywnoscOd!) ? null : 'ostrzez';
}
